// GatewayHealthModal.js
// Modal that displays gateway health timeline similar to SaaS portal

import React, { useEffect, useState } from 'react';
import './GatewayHealthModal.css';

export default function GatewayHealthModal({ gwip, gwmac, onClose }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [gatewayInfo, setGatewayInfo] = useState(null);
  const [healthData, setHealthData] = useState([]);
  const [hoveredBar, setHoveredBar] = useState(null);

  useEffect(() => {
    if (!gwip || !gwmac) return;

    async function fetchData() {
      try {
        setLoading(true);
        setError(null);

        // Step 1: Fetch gateway config to get company/store codes
        const configRes = await fetch(`/api/gateway-info/config?gwip=${encodeURIComponent(gwip)}`, {
          credentials: 'include'
        });

        if (!configRes.ok) {
          throw new Error(`Failed to fetch gateway config: ${configRes.statusText}`);
        }

        const config = await configRes.json();

        // Extract info from config
        const verDaemon = config.verDaemon || '';
        const gatewayMac = config.mac || ''; // Use MAC from gateway config
        const siteCode = config.siteCode || '';

        // Hardcode company/store for SaaS API compatibility
        const company = 'WLAB';
        const store = '100';

        if (!gatewayMac) {
          throw new Error('Gateway config missing MAC address');
        }

        setGatewayInfo({ company, store, verDaemon, gwip, gwmac: gatewayMac, siteCode });

        // Step 2: Fetch health timeline (last 6 hours for faster loading)
        const endTime = new Date();
        const startTime = new Date(endTime.getTime() - 6 * 60 * 60 * 1000);

        const formatTime = (d) => d.toISOString().slice(0, 19).replace('T', ' ');

        const healthRes = await fetch(
          `/api/gateway-info/health?company=${encodeURIComponent(company)}&store=${encodeURIComponent(store)}&gateway=${encodeURIComponent(gatewayMac)}&startTime=${encodeURIComponent(formatTime(startTime))}&endTime=${encodeURIComponent(formatTime(endTime))}`,
          { credentials: 'include' }
        );

        if (!healthRes.ok) {
          throw new Error(`Failed to fetch health data: ${healthRes.statusText}`);
        }

        const health = await healthRes.json();
        setHealthData(health.status || []);

      } catch (err) {
        console.error('Gateway health fetch error:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }

    fetchData();
  }, [gwip, gwmac]);

  // Render timeline bars
  function renderTimeline() {
    if (healthData.length === 0) {
      return <div style={{ padding: 20, textAlign: 'center', color: '#999' }}>No health data available</div>;
    }

    return (
      <div className="timeline-container">
        <div className={`timeline-tooltip-fixed ${hoveredBar === null ? 'hidden' : ''}`}>
          {hoveredBar !== null && healthData[hoveredBar] ? (
            <>
              <div className="tooltip-time">{healthData[hoveredBar].timeInterval}</div>
              <div className={`tooltip-status ${healthData[hoveredBar].status}`}>
                {healthData[hoveredBar].status.toUpperCase()}
              </div>
              <div className="tooltip-count">Count: {healthData[hoveredBar].count}</div>
            </>
          ) : (
            <>
              <div className="tooltip-time">--:--</div>
              <div className="tooltip-status">N/A</div>
              <div className="tooltip-count">Count: 0</div>
            </>
          )}
        </div>
        <div className="timeline-bars" style={{ marginTop: '120px' }}>
          {healthData.map((item, idx) => {
            const isOnline = item.status === 'online';
            const height = Math.max(item.count * 8, 30); // Scale height by count - increased from 2x to 8x
            return (
              <div
                key={idx}
                className={`timeline-bar ${isOnline ? 'online' : 'offline'} ${hoveredBar === idx ? 'hovered' : ''}`}
                style={{ height: `${height}px` }}
                onMouseEnter={() => setHoveredBar(idx)}
                onMouseLeave={() => setHoveredBar(null)}
                title={`${item.timeInterval} - ${item.status.toUpperCase()} (${item.count})`}
              />
            );
          })}
        </div>
        <div className="timeline-labels">
          {healthData.filter((_, idx) => idx % Math.ceil(healthData.length / 10) === 0).map((item, idx) => (
            <span key={idx} className="timeline-label">
              {item.timeInterval.slice(11, 16)}
            </span>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content gateway-health-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Gateway Timeline</h2>
          {gatewayInfo && (
            <div className="gateway-mac">MAC ID: {gatewayInfo.gwmac}</div>
          )}
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        <div className="modal-body">
          {loading && (
            <div className="loading-state">
              <div className="spinner"></div>
              <p>Loading gateway information...</p>
            </div>
          )}

          {error && (
            <div className="error-state">
              <p style={{ color: '#e74c3c' }}>Error: {error}</p>
            </div>
          )}

          {!loading && !error && gatewayInfo && (
            <>
              <div className="gateway-info-grid">
                <div className="info-item">
                  <label>Gateway IP</label>
                  <value>{gatewayInfo.gwip}</value>
                </div>
                <div className="info-item">
                  <label>Site Code</label>
                  <value>{gatewayInfo.siteCode}</value>
                </div>
                <div className="info-item">
                  <label>Store</label>
                  <value>{gatewayInfo.store}</value>
                </div>
                <div className="info-item">
                  <label>Firmware Version</label>
                  <value>{gatewayInfo.verDaemon || 'N/A'}</value>
                </div>
              </div>

              <div className="timeline-section">
                <h3>Health Timeline (Last 24 Hours)</h3>
                {renderTimeline()}
              </div>
            </>
          )}
        </div>

        <div className="modal-footer">
          <button className="btn-cancel" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}
